import React, { useEffect, useState } from 'react';
import axiosInstance from '../api';
import { useLang } from '../i18n/LanguageContext';

// Landing-page Success Stories: latest approved-NOC applications that have
// BOTH a BEFORE and AFTER photo. Public — no auth.
//
// Cards sit in a static grid (no marquee) and show each photo in full
// (object-contain on a neutral backdrop) so nothing gets cropped. Clicking a
// card opens a detail view with large before/after photos.

const Photo = ({ src, alt, label, labelCls, tall = false }) => (
  <figure className={`relative bg-slate-900 ${tall ? 'h-72 sm:h-96' : 'h-44'}`}>
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className="w-full h-full object-contain"
      onError={(e) => { e.currentTarget.style.opacity = 0.2; }}
    />
    <figcaption className={`absolute top-2 left-2 text-[11px] font-bold tracking-wider px-2 py-1 text-white ${labelCls}`}>
      {label}
    </figcaption>
  </figure>
);

const SuccessStories = () => {
  const { tr } = useLang();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    axiosInstance
      .get('/restoration/public/success-stories?limit=9', { silent: true })
      .then((res) => setStories(res.data?.data || []))
      .catch(() => setStories([]))
      .finally(() => setLoading(false));
  }, []);

  // Close the detail view on Escape and lock page scroll while it is open.
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => e.key === 'Escape' && setActive(null);
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [active]);

  if (loading || !stories.length) return null;

  const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('en-IN') : '—');
  const before = tr('BEFORE', 'முன்');
  const after = tr('AFTER', 'பின்');

  return (
    <section className="w-full py-10 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 text-center">
          <span className="text-xs font-semibold uppercase tracking-widest text-emerald-700">
            {tr('Success Stories', 'வெற்றிக் கதைகள்')}
          </span>
          <h2 className="mt-2 text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">
            {tr('Waterbody Conservation', 'நீர்நிலைப் பாதுகாப்பு')}
          </h2>
          <div className="w-16 h-1 bg-brand-accent mx-auto mt-4"></div>
          <p className="text-slate-500 mt-4 max-w-2xl mx-auto">
            {tr(
              'Restored water bodies across Tamil Nadu — a snapshot of the difference community-led conservation work is making, one water body at a time.',
              'தமிழ்நாடு முழுவதும் புனரமைக்கப்பட்ட நீர்நிலைகள் — சமூகப் பங்களிப்புடன் மேற்கொள்ளப்படும் பாதுகாப்புப் பணிகள் ஏற்படுத்தும் மாற்றத்தின் ஒரு பார்வை.'
            )}
          </p>
        </div>

        {/* Flex-wrap keeps a single story (or two) centred instead of left-aligned. */}
        <div className="flex flex-wrap justify-center gap-6">
          {stories.map((s) => (
            <button
              type="button"
              key={s.application_id}
              onClick={() => setActive(s)}
              className="w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] text-left bg-white border border-slate-200 border-t-4 border-t-emerald-600 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all"
            >
              <div className="grid grid-cols-2 gap-0.5 bg-slate-200">
                <Photo src={s.before_url} alt={tr('Before restoration', 'புனரமைப்புக்கு முன்')} label={before} labelCls="bg-slate-800/90" />
                <Photo src={s.after_url} alt={tr('After restoration', 'புனரமைப்புக்குப் பின்')} label={after} labelCls="bg-emerald-600/90" />
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-slate-900 leading-snug">{s.tank_name || tr('Water body', 'நீர்நிலை')}</h3>
                <p className="text-sm text-slate-500 mt-1">
                  {s.district_name || '—'}
                  {s.department_code ? ` · ${s.department_code}` : ''}
                </p>
                <p className="mt-3 text-sm font-medium text-brand-700">{tr('View details →', 'விவரங்களைக் காண்க →')}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Detail view */}
      {active && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={() => setActive(null)}>
          <div className="bg-white w-full max-w-5xl max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="bg-brand-800 border-b-4 border-brand-accent px-5 py-3 flex items-start justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-widest text-brand-100">{tr('Success Story', 'வெற்றிக் கதை')}</p>
                <h3 className="text-lg font-semibold text-white">{active.tank_name || tr('Water body', 'நீர்நிலை')}</h3>
              </div>
              <button type="button" onClick={() => setActive(null)} className="text-white/80 hover:text-white text-2xl leading-none" aria-label="Close">×</button>
            </div>

            <div className="grid md:grid-cols-2 gap-1 bg-slate-200">
              <Photo src={active.before_url} alt={tr('Before restoration', 'புனரமைப்புக்கு முன்')} label={before} labelCls="bg-slate-800/90" tall />
              <Photo src={active.after_url} alt={tr('After restoration', 'புனரமைப்புக்குப் பின்')} label={after} labelCls="bg-emerald-600/90" tall />
            </div>

            <table className="w-full text-sm">
              <tbody>
                {[
                  [tr('Water Body', 'நீர்நிலை'), active.tank_name],
                  [tr('District', 'மாவட்டம்'), active.district_name],
                  [tr('Department', 'துறை'), active.department_name || active.department_code],
                  [tr('Restored by', 'புனரமைத்தவர்'), active.ngo_name],
                  [tr('NOC ID', 'தடையின்மைச் சான்று எண்'), `NOC${String(active.application_id).padStart(3, '0')}`],
                  [tr('Work Completed On', 'பணி நிறைவு நாள்'), fmtDate(active.completion_issued_at)],
                ].map(([k, v]) => (
                  <tr key={k} className="odd:bg-white even:bg-brand-50 border-b border-slate-100">
                    <td className="px-5 py-2.5 w-56 text-xs font-semibold uppercase tracking-wide text-slate-500">{k}</td>
                    <td className="px-5 py-2.5 text-slate-900">{v || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
};

export default SuccessStories;
